import { OrderHealthMetricsResponseObject } from "@fintechprimitives/fpapi";
import { ValueFormatterParams } from "ag-grid-community";
import {
  HealthMetricsRowData,
  HealthMetricsRowDataType,
  OrderHealthMetricWithRange,
} from "./types";

export const formatLatencyValue = (params: ValueFormatterParams) => {
  const value = params.value;
  return value !== null && value !== undefined ? +value.toString().slice(0, 5) : "-";
};

export const splitDateRangeIntoThreeIntervals = (_startDate: string, _endDate: string) => {
  const startDate = new Date(_startDate);
  const endDate = new Date(_endDate);

  // Calculate total days (inclusive of both start and end dates)
  const totalDays =
    Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  // If less than 3 days, return the same date range
  if (totalDays < 3) {
    return [{ startDate: _startDate, endDate: _endDate }];
  }

  const base = Math.floor(totalDays / 3);
  const remainder = totalDays % 3;

  let interval1Days: number, interval2Days: number, interval3Days: number;

  if (remainder === 0) {
    // Perfectly divisible by 3
    interval1Days = base;
    interval2Days = base;
    interval3Days = base;
  } else if (remainder === 1) {
    // e.g., 16 days -> 5, 5, 6 (last column gets extra day)
    interval1Days = base;
    interval2Days = base;
    interval3Days = base + 1;
  } else {
    // remainder === 2
    // e.g., 17 days -> 6, 6, 5 (first two columns get extra day)
    interval1Days = base + 1;
    interval2Days = base + 1;
    interval3Days = base;
  }

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const interval1Start = startDate;
  const interval1End = addDays(interval1Start, interval1Days - 1); // -1 because we want to include the end date

  const interval2Start = addDays(interval1End, 1);
  const interval2End = addDays(interval2Start, interval2Days - 1);

  const interval3Start = addDays(interval2End, 1);
  const interval3End = addDays(interval3Start, interval3Days - 1);

  return [
    { startDate: formatDate(interval1Start), endDate: formatDate(interval1End) },
    { startDate: formatDate(interval2Start), endDate: formatDate(interval2End) },
    { startDate: formatDate(interval3Start), endDate: formatDate(interval3End) },
  ];
};

const convertStartDateAndEndDateToRangeString = (
  startDate: string | undefined,
  endDate: string | undefined
) => {
  if (!startDate || !endDate) return null;

  return `${startDate}:${endDate}`;
};

const getThreeDateRanges = (dateRanges: ReturnType<typeof splitDateRangeIntoThreeIntervals>) => {
  const range1 = convertStartDateAndEndDateToRangeString(
    dateRanges?.[0]?.startDate,
    dateRanges?.[0]?.endDate
  );
  const range2 = convertStartDateAndEndDateToRangeString(
    dateRanges?.[1]?.startDate,
    dateRanges?.[1]?.endDate
  );
  const range3 = convertStartDateAndEndDateToRangeString(
    dateRanges?.[2]?.startDate,
    dateRanges?.[2]?.endDate
  );

  return [range1, range2, range3];
};

const getTotalOrdersCreatedForARange = (
  orderMetrics: (OrderHealthMetricWithRange | undefined)[],
  range: string | null
) => {
  if (!range) return null;

  return (
    orderMetrics.find((metric) => {
      return metric?.total_created && metric.range === range;
    })?.total_created ?? null
  );
};

const getTotalOrdersMovedToMetricTypeForARange = (
  orderMetrics: (OrderHealthMetricWithRange | undefined)[],
  range: string | null,
  metricType: "review_completed" | "submitted" | "activated"
) => {
  if (!range) return null;

  return (
    orderMetrics.find((metric) => {
      return metric?.metric_type === metricType && metric.range === range && metric.total_weight;
    })?.total_weight ?? null
  );
};

const getMetricValueForARange = (
  orderMetrics: (OrderHealthMetricWithRange | undefined)[],
  range: string | null,
  metricType: "review_completed" | "submitted" | "activated",
  metricPercentile: "p100" | "p99" | "p95" | "p90"
) => {
  if (!range) return null;

  return (
    orderMetrics.find((metric) => {
      return (
        metric?.metric_type === metricType &&
        metric.range === range &&
        metric[`weighted_${metricPercentile}`]
      );
    })?.[`weighted_${metricPercentile}`] ?? null
  );
};

const transformPurchaseOrders = (
  purchaseOrders: (OrderHealthMetricWithRange | undefined)[],
  dateRanges: ReturnType<typeof splitDateRangeIntoThreeIntervals>
) => {
  const [range1, range2, range3] = getThreeDateRanges(dateRanges);

  const desiredOrder = [
    {
      metric: "Total lumpsum purchases created",
      target: null,
      type: "string",
      range1ValueGetter: () => getTotalOrdersCreatedForARange(purchaseOrders, range1),
      range2ValueGetter: () => getTotalOrdersCreatedForARange(purchaseOrders, range2),
      range3ValueGetter: () => getTotalOrdersCreatedForARange(purchaseOrders, range3),
    },
    {
      metric: "Lumpsum purchase review completion",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 2 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range1, "review_completed", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range2, "review_completed", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range3, "review_completed", metricPercentile),
      };
    }),
    {
      metric: "Lumpsum purchases moved to review completed",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range1, "review_completed"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range2, "review_completed"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range3, "review_completed"),
    },
    {
      metric: "Lumpsum purchase submission",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 4 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range1, "submitted", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range2, "submitted", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(purchaseOrders, range3, "submitted", metricPercentile),
      };
    }),
    {
      metric: "Total lumpsum purchases submitted",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range1, "submitted"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range2, "submitted"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(purchaseOrders, range3, "submitted"),
    },
  ];

  return desiredOrder.map((purchaseMetric) => {
    return {
      metric: purchaseMetric.metric,
      target: purchaseMetric.target,
      type: purchaseMetric.type as HealthMetricsRowDataType,
      range1: purchaseMetric.range1ValueGetter(),
      range2: purchaseMetric.range2ValueGetter(),
      range3: purchaseMetric.range3ValueGetter(),
    };
  });
};

const transformRedemptionOrders = (
  redemptionOrders: (OrderHealthMetricWithRange | undefined)[],
  dateRanges: ReturnType<typeof splitDateRangeIntoThreeIntervals>
) => {
  const [range1, range2, range3] = getThreeDateRanges(dateRanges);

  const desiredOrder = [
    {
      metric: "Total redemptions created",
      target: null,
      type: "string",
      range1ValueGetter: () => getTotalOrdersCreatedForARange(redemptionOrders, range1),
      range2ValueGetter: () => getTotalOrdersCreatedForARange(redemptionOrders, range2),
      range3ValueGetter: () => getTotalOrdersCreatedForARange(redemptionOrders, range3),
    },
    {
      metric: "Redemption review completion",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 2 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range1, "review_completed", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range2, "review_completed", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range3, "review_completed", metricPercentile),
      };
    }),
    {
      metric: "Redemptions moved to review completed",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range1, "review_completed"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range2, "review_completed"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range3, "review_completed"),
    },
    {
      metric: "Redemption submission",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 4 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range1, "submitted", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range2, "submitted", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(redemptionOrders, range3, "submitted", metricPercentile),
      };
    }),
    {
      metric: "Total redemptions submitted",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range1, "submitted"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range2, "submitted"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(redemptionOrders, range3, "submitted"),
    },
  ];

  return desiredOrder.map((redemptionMetric) => {
    return {
      metric: redemptionMetric.metric,
      target: redemptionMetric.target,
      type: redemptionMetric.type as HealthMetricsRowDataType,
      range1: redemptionMetric.range1ValueGetter(),
      range2: redemptionMetric.range2ValueGetter(),
      range3: redemptionMetric.range3ValueGetter(),
    };
  });
};

const transformSipOrders = (
  sipOrders: (OrderHealthMetricWithRange | undefined)[],
  dateRanges: ReturnType<typeof splitDateRangeIntoThreeIntervals>
) => {
  const [range1, range2, range3] = getThreeDateRanges(dateRanges);

  const desiredOrder = [
    {
      metric: "Total purchase plans created",
      target: null,
      type: "string",
      range1ValueGetter: () => getTotalOrdersCreatedForARange(sipOrders, range1),
      range2ValueGetter: () => getTotalOrdersCreatedForARange(sipOrders, range2),
      range3ValueGetter: () => getTotalOrdersCreatedForARange(sipOrders, range3),
    },
    {
      metric: "Purchase plan review completion",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 2 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(sipOrders, range1, "review_completed", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(sipOrders, range2, "review_completed", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(sipOrders, range3, "review_completed", metricPercentile),
      };
    }),
    {
      metric: "Purchase plans moved to review completed",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range1, "review_completed"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range2, "review_completed"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range3, "review_completed"),
    },
    {
      metric: "Purchase plan activation",
      target: null,
      type: "label",
      range1ValueGetter: () => null,
      range2ValueGetter: () => null,
      range3ValueGetter: () => null,
    },
    ...(["p100", "p99", "p95", "p90"] as const).map((metricPercentile) => {
      return {
        metric: `${metricPercentile}(s)`,
        target: metricPercentile === "p99" ? 4 : null,
        type: "number",
        range1ValueGetter: () =>
          getMetricValueForARange(sipOrders, range1, "activated", metricPercentile),
        range2ValueGetter: () =>
          getMetricValueForARange(sipOrders, range2, "activated", metricPercentile),
        range3ValueGetter: () =>
          getMetricValueForARange(sipOrders, range3, "activated", metricPercentile),
      };
    }),
    {
      metric: "Total purchase plans activated",
      target: null,
      type: "string",
      range1ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range1, "activated"),
      range2ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range2, "activated"),
      range3ValueGetter: () =>
        getTotalOrdersMovedToMetricTypeForARange(sipOrders, range3, "activated"),
    },
  ];

  return desiredOrder.map((sipMetric) => {
    return {
      metric: sipMetric.metric,
      target: sipMetric.target,
      type: sipMetric.type as HealthMetricsRowDataType,
      range1: sipMetric.range1ValueGetter(),
      range2: sipMetric.range2ValueGetter(),
      range3: sipMetric.range3ValueGetter(),
    };
  });
};

export const transformHealthMetricsData = (
  promisesResponseData: (OrderHealthMetricsResponseObject | undefined)[],
  dateRanges: ReturnType<typeof splitDateRangeIntoThreeIntervals>
): [HealthMetricsRowData[], HealthMetricsRowData[], HealthMetricsRowData[]] => {
  // 1. Tag date range to data
  const dateRangeTaggedResponse = promisesResponseData
    .flatMap((rangeMetric, i) => {
      if (!rangeMetric) return undefined;

      return rangeMetric.data.map((metric) => {
        return {
          ...metric,
          range: convertStartDateAndEndDateToRangeString(
            dateRanges[i].startDate,
            dateRanges[i].endDate
          ),
        };
      });
    })
    .filter(Boolean);

  const purchaseOrders = dateRangeTaggedResponse.filter(
    (metric) => metric!.order_type === "lumpsum"
  );

  const redemptionOrders = dateRangeTaggedResponse.filter(
    (metric) => metric?.order_type === "redemption"
  );

  const sipOrders = dateRangeTaggedResponse.filter((metric) => metric?.order_type === "sip");

  return [
    transformPurchaseOrders(purchaseOrders, dateRanges),
    transformRedemptionOrders(redemptionOrders, dateRanges),
    transformSipOrders(sipOrders, dateRanges),
  ];
};

export const getDefaultHealthMetricsDateRange = () => {
  // Past 3 complete weeks (Monday to Sunday)
  const today = new Date();
  const dayOfWeek = today.getDay(); // Returns day of the week. 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  let endDate: Date;

  // If today is Sunday, go back to the previous sunday
  if (dayOfWeek === 0) {
    endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 7);
  } else {
    // Go back to sunday
    const daysToSubtract = dayOfWeek;
    endDate = new Date(today);
    endDate.setDate(endDate.getDate() - daysToSubtract);
  }

  // Start date is Monday of 3 weeks before the end date
  // endDate is a Sunday, so going back 20 days gives us the Monday of 3 weeks ago
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 20); // 3 weeks (21 days) - 1 day = Monday

  return {
    start_date: startDate.toISOString().split("T")[0],
    end_date: endDate.toISOString().split("T")[0],
  };
};
